import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/integrations/supabase/client';

interface UseWorkspaceOnboardingReturn {
  needsWorkspace: boolean;
  showModal: boolean;
  closeModal: () => void;
  loading: boolean;
  reopenModal: () => void;
}

export const useWorkspaceOnboarding = (): UseWorkspaceOnboardingReturn => {
  const { user, session, loading: authLoading } = useAuth();
  const [needsWorkspace, setNeedsWorkspace] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const checkWorkspaceNeeds = async () => {
      // Only check if user is authenticated and not loading
      if (!user || !session || authLoading) {
        setNeedsWorkspace(false);
        setShowModal(false);
        return;
      }

      // Skip workspace check if on invitation routes
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/invite/')) {
        setNeedsWorkspace(false);
        setShowModal(false);
        return;
      }

      setLoading(true);

      try {
        // Check if user has any workspace memberships
        const { data: memberships, error } = await (supabase as any)
          .from('workspace_memberships')
          .select('id, workspace_id, role, status')
          .eq('user_id', user.id)
          .eq('status', 'active');

        if (error) {
          console.error('Error checking workspace memberships:', error);
          setNeedsWorkspace(false);
          setShowModal(false);
          return;
        }

        const hasWorkspaces = memberships && memberships.length > 0;
        setNeedsWorkspace(!hasWorkspaces);
        
        // Show modal if user needs workspace and isn't already showing it
        if (!hasWorkspaces && !showModal) {
          setShowModal(true);
        }

      } catch (error) {
        console.error('Error in workspace onboarding check:', error);
        setNeedsWorkspace(false);
        setShowModal(false);
      } finally {
        setLoading(false);
      }
    };

    checkWorkspaceNeeds();
  }, [user, session, authLoading]);

  const closeModal = () => {
    setShowModal(false);
  };

  const reopenModal = () => {
    if (needsWorkspace) {
      setShowModal(true);
    }
  };

  return {
    needsWorkspace,
    showModal,
    closeModal,
    loading,
    reopenModal
  };
};